import { useEffect, useState } from "react";

// The browser fires beforeinstallprompt once, often before React mounts — capture it at module load.
let deferredPrompt = null;
const listeners = new Set();
const notify = () => listeners.forEach((l) => l());

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event) => {
    event.preventDefault(); // show our own Install button instead of the mini-infobar
    deferredPrompt = event;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notify();
  });
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

function isIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iphone|ipad|ipod/i.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

/**
 * PWA install state:
 * - canInstall: browser offered installation (Chrome/Edge/Android) → call install()
 * - showIosHint: iPhone/iPad Safari, not installed → show "Share → Add to Home Screen"
 * - installed: running as an installed app
 */
export function useInstallPrompt() {
  const [, force] = useState(0);
  const [installed, setInstalled] = useState(isStandalone);

  useEffect(() => {
    const rerender = () => {
      setInstalled(isStandalone());
      force((n) => n + 1);
    };
    listeners.add(rerender);
    const mq = window.matchMedia?.("(display-mode: standalone)");
    mq?.addEventListener?.("change", rerender);
    return () => {
      listeners.delete(rerender);
      mq?.removeEventListener?.("change", rerender);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return "unavailable";
    const promptEvent = deferredPrompt;
    deferredPrompt = null; // a prompt can only be used once
    promptEvent.prompt();
    const { outcome } = await promptEvent.userChoice;
    notify();
    return outcome; // 'accepted' | 'dismissed'
  };

  return {
    canInstall: !installed && !!deferredPrompt,
    showIosHint: !installed && isIos(),
    installed,
    install
  };
}

export default useInstallPrompt;
